import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import {
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Legend,
} from "recharts";
import {
  TrendingUp,
  Wallet,
  Gauge,
  FileSignature,
  Receipt,
  Percent,
  Layers,
  ClipboardList,
  FolderOpen,
  Bell,
  AlertTriangle,
  Plus,
  FilePlus2,
  PackagePlus,
} from "lucide-react";
import api from "../../lib/api";
import { eur, pct, margeText } from "../../lib/format";
import { useViewRole } from "../../context/ViewRoleContext";
import { ChartTooltip } from "../../lib/ChartTooltip";
import { ScopeSwitcher } from "../common/ScopeSwitcher";

function SectionTitle({ label, hint }) {
  return (
    <div className="flex items-center gap-3 mt-2 mb-1">
      <h2 className="text-[11px] font-bold uppercase tracking-[0.18em] text-slate-500">{label}</h2>
      <div className="flex-1 h-px bg-[#1E293B]" />
      {hint && <span className="text-[11px] text-slate-600">{hint}</span>}
    </div>
  );
}

function Kpi({ icon: Icon, title, value, sub, color, subtle, testId, hidden, onClick }) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 12 }}
      animate={{ opacity: 1, y: 0 }}
      onClick={onClick}
      role={onClick ? "button" : undefined}
      className={`rounded-xl bg-white border border-slate-200 shadow-[0_2px_10px_rgba(31,65,100,0.05)] p-5 transition-colors ${onClick ? "cursor-pointer hover:border-blue-200 hover:bg-slate-50" : ""}`}
      data-testid={testId}
    >
      <div className="flex items-center justify-between">
        <span className="text-xs uppercase tracking-wider text-slate-500 font-medium">{title}</span>
        <div className="h-8 w-8 rounded-lg flex items-center justify-center" style={{ background: subtle }}>
          <Icon className="h-4 w-4" style={{ color }} />
        </div>
      </div>
      <div className="mt-3 font-mono-metric text-2xl sm:text-3xl font-bold tracking-tight">
        {hidden ? <span className="text-slate-600">•••••</span> : value}
      </div>
      <div className="mt-1 text-xs text-slate-500">{sub}</div>
    </motion.div>
  );
}

export default function DashboardOverview() {
  const { canSeeMargins, isAdmin } = useViewRole();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [scope, setScope] = useState("real");

  const KPI_STORAGE_KEY = "crm-dashboard-kpis-v1";

  const KPI_DEFAULTS = {
    potentiel: true,
    signe: true,
    commande: true,
    livre: true,
    facture: true,
    encaisse: true,
    transformation: true,
  };

  const [visibleKpis, setVisibleKpis] = useState(() => {
    try {
      const saved = localStorage.getItem(KPI_STORAGE_KEY);
      return saved ? { ...KPI_DEFAULTS, ...JSON.parse(saved) } : KPI_DEFAULTS;
    } catch {
      return KPI_DEFAULTS;
    }
  });

  const [showKpiSettings, setShowKpiSettings] = useState(false);

  const CHART_STORAGE_KEY = "crm-dashboard-chart-v1";

  const CHART_DEFAULTS = {
    potentiel: true,
    signe: true,
    commande: false,
    livre: false,
    facture: true,
    encaisse: false,
  };

  const [visibleChartLines, setVisibleChartLines] = useState(() => {
    try {
      const saved = localStorage.getItem(CHART_STORAGE_KEY);
      return saved ? { ...CHART_DEFAULTS, ...JSON.parse(saved) } : CHART_DEFAULTS;
    } catch {
      return CHART_DEFAULTS;
    }
  });

  useEffect(() => {
    try {
      localStorage.setItem(CHART_STORAGE_KEY, JSON.stringify(visibleChartLines));
    } catch {}
  }, [visibleChartLines]);

  const toggleChartLine = (key) => {
    setVisibleChartLines((prev) => ({
      ...prev,
      [key]: !prev[key],
    }));
  };

  const resetChartLines = () => {
    setVisibleChartLines(CHART_DEFAULTS);
  };

  useEffect(() => {
    try {
      localStorage.setItem(KPI_STORAGE_KEY, JSON.stringify(visibleKpis));
    } catch {}
  }, [visibleKpis]);

  const toggleKpi = (key) => {
    setVisibleKpis((prev) => ({
      ...prev,
      [key]: !prev[key],
    }));
  };

  const resetKpis = () => {
    setVisibleKpis(KPI_DEFAULTS);
  };

  useEffect(() => {
    const load = () => api.get(`/dashboard?scope=${scope}`).then((r) => setData(r.data)).catch(() => {});
    load();
    window.addEventListener("crm:data-changed", load);
    return () => window.removeEventListener("crm:data-changed", load);
  }, [scope]);

  if (!data) return <div className="text-slate-500">Chargement du tableau de bord…</div>;

  const empty = scope === "real" && data.nb_total_devis === 0;

  return (
    <div className="space-y-6" data-testid="dashboard-view">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <h1 className="font-head text-[30px] font-extrabold tracking-tight text-[#102A4C]">Tableau de Bord</h1>
          <p className="text-slate-500 text-sm mt-1">Pilotage commercial : performance, rentabilité et pipeline.</p>
        </div>
        {isAdmin && <ScopeSwitcher scope={scope} setScope={setScope} />}
      </div>

      {empty && (
        <div data-testid="dashboard-empty-real" className="rounded-xl border border-amber-500/20 bg-amber-500/5 p-4 text-sm text-amber-300">
          Aucune donnée réelle pour l'instant. Importez votre historique commercial ou basculez sur « Démo » pour explorer l'application.
        </div>
      )}

      {/* ---------------- PERFORMANCE COMMERCIALE ---------------- */}
      <div className="flex items-end justify-between gap-4 mt-2 mb-1">
        <SectionTitle
          label="Performance commerciale"
          hint={`${data.nb_total_devis} dossiers`}
        />

        <button
          type="button"
          onClick={() => setShowKpiSettings((v) => !v)}
          className="shrink-0 inline-flex items-center gap-2 px-3 py-1.5 rounded-lg border border-slate-200 bg-white text-xs font-semibold text-slate-600 hover:text-[#102A4C] hover:border-blue-200 transition-colors"
        >
          ⚙️ Personnaliser
        </button>
      </div>

      {showKpiSettings && (
        <div className="rounded-xl border border-slate-200 bg-white p-4 shadow-[0_2px_10px_rgba(31,65,100,0.05)]">
          <div className="flex flex-wrap items-center justify-between gap-3 mb-3">
            <div>
              <div className="text-sm font-semibold text-[#102A4C]">
                Indicateurs affichés
              </div>
              <div className="text-xs text-slate-500 mt-0.5">
                Affiche uniquement les KPI utiles à ton activité.
              </div>
            </div>

            <button
              type="button"
              onClick={resetKpis}
              className="text-xs font-semibold text-blue-600 hover:text-blue-700"
            >
              Tout afficher
            </button>
          </div>

          <div className="flex flex-wrap gap-2">
            {[
              ["potentiel", "CA potentiel"],
              ["signe", "CA signé"],
              ["commande", "CA commandé"],
              ["livre", "CA livré"],
              ["facture", "CA facturé"],
              ["encaisse", "CA encaissé"],
              ["transformation", "Taux de transformation"],
            ].map(([key, label]) => (
              <button
                key={key}
                type="button"
                onClick={() => toggleKpi(key)}
                className={`inline-flex items-center gap-2 px-3 py-2 rounded-lg border text-xs font-semibold transition-colors ${
                  visibleKpis[key]
                    ? "border-blue-200 bg-blue-50 text-blue-700"
                    : "border-slate-200 bg-slate-50 text-slate-400"
                }`}
              >
                <span
                  className={`h-2 w-2 rounded-full ${
                    visibleKpis[key] ? "bg-blue-500" : "bg-slate-300"
                  }`}
                />
                {label}
              </button>
            ))}
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-6 gap-4">
        {visibleKpis.potentiel && (
          <Kpi
            testId="kpi-ca-potentiel"
            icon={TrendingUp}
            title="CA Potentiel HT"
            value={eur(data.ca_potentiel_ht)}
            sub={`${data.devis_ouverts_count} dossiers ouverts`}
            color="#38BDF8"
            subtle="rgba(56,189,248,0.15)"
          />
        )}

        {visibleKpis.signe && (
          <Kpi
            testId="kpi-ca-signe"
            icon={FileSignature}
            title="CA Signé HT"
            value={eur(data.ca_signe_ht)}
            sub={`${data.nb_signes} dossiers signés`}
            color="#10B981"
            subtle="rgba(16,185,129,0.15)"
          />
        )}

        {visibleKpis.commande && (
          <Kpi
            testId="kpi-ca-commande"
            icon={PackagePlus}
            title="CA Commandé HT"
            value={eur(data.ca_commande_ht)}
            sub="Date de commande"
            color="#0EA5E9"
            subtle="rgba(14,165,233,0.15)"
          />
        )}

        {visibleKpis.livre && (
          <Kpi
            testId="kpi-ca-livre"
            icon={PackagePlus}
            title="CA Livré HT"
            value={eur(data.ca_livre_ht)}
            sub="Date de livraison"
            color="#8B5CF6"
            subtle="rgba(139,92,246,0.15)"
          />
        )}

        {visibleKpis.facture && (
          <Kpi
            testId="kpi-ca-facture"
            icon={Receipt}
            title="CA Facturé HT"
            value={eur(data.ca_facture_ht)}
            sub="Date de facturation"
            color="#F59E0B"
            subtle="rgba(245,158,11,0.15)"
          />
        )}

        {visibleKpis.encaisse && (
          <Kpi
            testId="kpi-ca-encaisse"
            icon={Wallet}
            title="CA Encaissé HT"
            value={eur(data.ca_encaisse_ht)}
            sub="Date d'encaissement"
            color="#14B8A6"
            subtle="rgba(20,184,166,0.15)"
          />
        )}
      </div>

      {visibleKpis.transformation && (
        <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-4">
          <Kpi
            testId="kpi-taux-transfo"
            icon={Percent}
            title="Taux de transformation"
            value={pct(data.taux_transformation_pct)}
            sub={`${data.nb_signes}/${data.nb_total_devis} dossiers`}
            color="#A78BFA"
            subtle="rgba(167,139,250,0.15)"
          />
        </div>
      )}

      {/* ---------------- RENTABILITÉ ---------------- */}
      <SectionTitle label="Rentabilité" hint="marge historique (source de vérité)" />
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 lg:col-span-1">
          <Kpi
            testId="kpi-marge-realisee"
            icon={Wallet}
            title="Marge signée HT"
            value={eur(data.marge_signee_ht)}
            sub="Marge historique / signés"
            color="#10B981"
            subtle="rgba(16,185,129,0.15)"
            hidden={!canSeeMargins}
          />
          <Kpi
            testId="kpi-taux-marge"
            icon={Gauge}
            title="Taux de marge"
            value={pct(data.taux_marge_signe_pct)}
            sub="Marge / CA signé"
            color="#F59E0B"
            subtle="rgba(245,158,11,0.15)"
            hidden={!canSeeMargins}
          />
        </div>

        <div className="lg:col-span-2 rounded-xl bg-white border border-slate-200 shadow-[0_2px_10px_rgba(31,65,100,0.05)] overflow-hidden" data-testid="marge-par-famille">
          <div className="flex items-center gap-2 px-5 py-3.5 border-b border-slate-200">
            <Layers className="h-4 w-4 text-sky-400" />
            <h3 className="font-head text-sm font-bold">Marge par famille</h3>
          </div>
          {(!data.marge_par_famille || data.marge_par_famille.length === 0) ? (
            <div className="p-5 text-sm text-slate-500">Aucune donnée signée à répartir.</div>
          ) : (
            <div className="overflow-x-auto max-h-[220px] overflow-y-auto">
              <table className="w-full text-sm">
                <thead className="sticky top-0 bg-white">
                  <tr className="text-left text-xs uppercase tracking-wider text-slate-500 border-b border-slate-200">
                    <th className="px-5 py-2.5 font-medium">Famille</th>
                    <th className="px-3 py-2.5 font-medium text-right">Signés</th>
                    <th className="px-3 py-2.5 font-medium text-right">CA signé</th>
                    <th className="px-3 py-2.5 font-medium text-right">Marge</th>
                    <th className="px-3 py-2.5 font-medium text-right">Taux</th>
                  </tr>
                </thead>
                <tbody>
                  {data.marge_par_famille.map((f) => (
                    <tr key={f.famille} className="border-b border-slate-200/60 hover:bg-slate-50/70" data-testid={`marge-fam-${f.famille}`}>
                      <td className="px-5 py-2.5 font-medium text-slate-200">{f.famille}</td>
                      <td className="px-3 py-2.5 text-right text-slate-400 font-mono-metric">{f.nb}</td>
                      <td className="px-3 py-2.5 text-right font-mono-metric">{eur(f.ca)}</td>
                      <td className="px-3 py-2.5 text-right font-mono-metric text-emerald-400">
                        {canSeeMargins ? eur(f.marge) : "•••"}
                      </td>
                      <td className={`px-3 py-2.5 text-right font-mono-metric font-semibold ${margeText(f.taux_marge_pct)}`}>
                        {canSeeMargins ? pct(f.taux_marge_pct) : "•••"}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      {/* ---------------- PIPELINE / ACTIVITÉ ---------------- */}
      <SectionTitle label="Pipeline / Activité" />
      <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-4">
        <Kpi
          testId="kpi-devis-ouverts"
          icon={ClipboardList}
          title="Devis ouverts"
          value={data.devis_ouverts_count}
          sub="En cours de négociation"
          color="#38BDF8"
          subtle="rgba(56,189,248,0.15)"
        />
        <Kpi
          testId="kpi-pipeline-montant"
          icon={TrendingUp}
          title="Montant du pipeline"
          value={eur(data.pipeline_montant_ht)}
          sub="Potentiel non signé"
          color="#A78BFA"
          subtle="rgba(167,139,250,0.15)"
        />
        <Kpi
          testId="kpi-projets-ouverts"
          icon={FolderOpen}
          title="Projets ouverts"
          value={data.projets_ouverts_count}
          sub="Chantiers non clôturés"
          color="#22D3EE"
          subtle="rgba(34,211,238,0.15)"
        />
        <Kpi
          testId="kpi-devis-relancer"
          icon={Bell}
          title="Devis à relancer"
          value={data.devis_a_relancer_count}
          sub="Relance due ou dépassée · cliquer"
          color="#F59E0B"
          subtle="rgba(245,158,11,0.15)"
          onClick={() => navigate("/actions?focus=retard")}
        />
      </div>

      {/* ---------------- ÉVOLUTION 6 MOIS ---------------- */}
      <div className="flex items-end justify-between gap-4 mt-2 mb-1">
        <SectionTitle label="Évolution 6 mois" hint="dates métier" />

        <button
          type="button"
          onClick={() => setShowKpiSettings((v) => !v)}
          className="shrink-0 inline-flex items-center gap-2 px-3 py-1.5 rounded-lg border border-slate-200 bg-white text-xs font-semibold text-slate-600 hover:text-[#102A4C] hover:border-blue-200 transition-colors"
        >
          📈 Courbes
        </button>
      </div>

      {showKpiSettings && (
        <div className="mb-4 rounded-xl border border-slate-200 bg-white p-4 shadow-[0_2px_10px_rgba(31,65,100,0.05)]">
          <div className="flex flex-wrap items-center justify-between gap-3 mb-3">
            <div>
              <div className="text-sm font-semibold text-[#102A4C]">
                Courbes affichées
              </div>
              <div className="text-xs text-slate-500 mt-0.5">
                Sélectionne les étapes du cycle commercial à comparer.
              </div>
            </div>

            <button
              type="button"
              onClick={resetChartLines}
              className="text-xs font-semibold text-blue-600 hover:text-blue-700"
            >
              Réinitialiser
            </button>
          </div>

          <div className="flex flex-wrap gap-2">
            {[
              ["potentiel", "CA potentiel"],
              ["signe", "CA signé"],
              ["commande", "CA commandé"],
              ["livre", "CA livré"],
              ["facture", "CA facturé"],
              ["encaisse", "CA encaissé"],
            ].map(([key, label]) => (
              <button
                key={key}
                type="button"
                onClick={() => toggleChartLine(key)}
                className={`inline-flex items-center gap-2 px-3 py-2 rounded-lg border text-xs font-semibold transition-colors ${
                  visibleChartLines[key]
                    ? "border-blue-200 bg-blue-50 text-blue-700"
                    : "border-slate-200 bg-slate-50 text-slate-400"
                }`}
              >
                <span
                  className={`h-2 w-2 rounded-full ${
                    visibleChartLines[key] ? "bg-blue-500" : "bg-slate-300"
                  }`}
                />
                {label}
              </button>
            ))}
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <div className="lg:col-span-2 rounded-xl bg-white border border-slate-200 shadow-[0_2px_10px_rgba(31,65,100,0.05)] p-5">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h2 className="font-head text-lg font-bold">Parcours commercial</h2>
              <span className="text-xs text-slate-500">
                Création → acceptation → commande → livraison → facturation → encaissement
              </span>
            </div>
          </div>

          <ResponsiveContainer width="100%" height={280}>
            <AreaChart data={data.evolution_c2} margin={{ left: -10, right: 8, top: 8 }}>
              <defs>
                <linearGradient id="gPot" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor="#38BDF8" stopOpacity={0.25} />
                  <stop offset="95%" stopColor="#38BDF8" stopOpacity={0} />
                </linearGradient>
                <linearGradient id="gSig" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor="#10B981" stopOpacity={0.25} />
                  <stop offset="95%" stopColor="#10B981" stopOpacity={0} />
                </linearGradient>
                <linearGradient id="gCmd" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor="#0EA5E9" stopOpacity={0.20} />
                  <stop offset="95%" stopColor="#0EA5E9" stopOpacity={0} />
                </linearGradient>
                <linearGradient id="gLiv" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor="#8B5CF6" stopOpacity={0.20} />
                  <stop offset="95%" stopColor="#8B5CF6" stopOpacity={0} />
                </linearGradient>
                <linearGradient id="gFac" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor="#F59E0B" stopOpacity={0.20} />
                  <stop offset="95%" stopColor="#F59E0B" stopOpacity={0} />
                </linearGradient>
                <linearGradient id="gEnc" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor="#14B8A6" stopOpacity={0.20} />
                  <stop offset="95%" stopColor="#14B8A6" stopOpacity={0} />
                </linearGradient>
              </defs>

              <CartesianGrid stroke="#1E293B" strokeDasharray="3 3" vertical={false} />
              <XAxis dataKey="mois" stroke="#64748B" fontSize={12} tickLine={false} axisLine={false} />
              <YAxis
                stroke="#64748B"
                fontSize={11}
                tickLine={false}
                axisLine={false}
                tickFormatter={(v) => `${v / 1000}k`}
              />
              <Tooltip content={<ChartTooltip formatter={eur} />} />
              <Legend wrapperStyle={{ fontSize: 12, color: "#94A3B8" }} />

              {visibleChartLines.potentiel && (
                <Area
                  type="monotone"
                  dataKey="ca_potentiel"
                  name="CA potentiel"
                  stroke="#38BDF8"
                  fill="url(#gPot)"
                  strokeWidth={2}
                />
              )}

              {visibleChartLines.signe && (
                <Area
                  type="monotone"
                  dataKey="ca_signe"
                  name="CA signé"
                  stroke="#10B981"
                  fill="url(#gSig)"
                  strokeWidth={2}
                />
              )}

              {visibleChartLines.commande && (
                <Area
                  type="monotone"
                  dataKey="ca_commande"
                  name="CA commandé"
                  stroke="#0EA5E9"
                  fill="url(#gCmd)"
                  strokeWidth={2}
                />
              )}

              {visibleChartLines.livre && (
                <Area
                  type="monotone"
                  dataKey="ca_livre"
                  name="CA livré"
                  stroke="#8B5CF6"
                  fill="url(#gLiv)"
                  strokeWidth={2}
                />
              )}

              {visibleChartLines.facture && (
                <Area
                  type="monotone"
                  dataKey="ca_facture"
                  name="CA facturé"
                  stroke="#F59E0B"
                  fill="url(#gFac)"
                  strokeWidth={2}
                />
              )}

              {visibleChartLines.encaisse && (
                <Area
                  type="monotone"
                  dataKey="ca_encaisse"
                  name="CA encaissé"
                  stroke="#14B8A6"
                  fill="url(#gEnc)"
                  strokeWidth={2}
                />
              )}
            </AreaChart>
          </ResponsiveContainer>
        </div>

        <div className="rounded-xl bg-white border border-slate-200 shadow-[0_2px_10px_rgba(31,65,100,0.05)] p-5">
          <h2 className="font-head text-lg font-bold mb-4">Actions rapides</h2>
          <div className="space-y-3">
            <button
              data-testid="quick-new-devis"
              onClick={() => navigate("/devis")}
              className="w-full flex items-center gap-3 p-3 rounded-lg bg-amber-500/10 border border-amber-500/20 text-amber-400 hover:bg-amber-500/15 transition-colors text-sm font-semibold"
            >
              <FilePlus2 className="h-4 w-4" /> Nouveau devis chiffré
            </button>
            <button
              data-testid="quick-relances"
              onClick={() => navigate("/actions")}
              className="w-full flex items-center gap-3 p-3 rounded-lg bg-[#0B0F17] border border-[#1E293B] text-slate-200 hover:border-slate-600 transition-colors text-sm font-semibold"
            >
              <Bell className="h-4 w-4" /> Voir les relances
            </button>
            <button
              data-testid="quick-new-prestation"
              onClick={() => navigate("/catalogue")}
              className="w-full flex items-center gap-3 p-3 rounded-lg bg-[#0B0F17] border border-[#1E293B] text-slate-200 hover:border-slate-600 transition-colors text-sm font-semibold"
            >
              <PackagePlus className="h-4 w-4" /> Ajouter une prestation
            </button>
          </div>
        </div>
      </div>

      {/* ---------------- Chantiers sous surveillance (si applicable) ---------------- */}
      {data.chantiers_alerte && data.chantiers_alerte.length > 0 && (
        <div className="rounded-xl bg-white border border-slate-200 shadow-[0_2px_10px_rgba(31,65,100,0.05)] overflow-hidden">
          <div className="flex items-center gap-2 px-5 py-4 border-b border-slate-200">
            <AlertTriangle className="h-4 w-4 text-red-400" />
            <h2 className="font-head text-lg font-bold">Chantiers sous surveillance</h2>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-xs uppercase tracking-wider text-slate-500 border-b border-slate-200">
                  <th className="px-5 py-2.5 font-medium">Chantier</th>
                  <th className="px-3 py-2.5 font-medium">Client</th>
                  <th className="px-3 py-2.5 font-medium text-right">CA facturé</th>
                  <th className="px-3 py-2.5 font-medium text-right">Coût réel</th>
                  <th className="px-3 py-2.5 font-medium text-right">Taux marge</th>
                </tr>
              </thead>
              <tbody>
                {data.chantiers_alerte.map((c) => (
                  <tr key={c.id} className="border-b border-slate-200/60 hover:bg-slate-50/70" data-testid={`alerte-chantier-${c.code}`}>
                    <td className="px-5 py-3 font-medium">{c.titre}</td>
                    <td className="px-3 py-3 text-slate-400">{c.client_nom}</td>
                    <td className="px-3 py-3 text-right font-mono-metric">{eur(c.ca_facture_ht)}</td>
                    <td className="px-3 py-3 text-right font-mono-metric text-orange-400">
                      {canSeeMargins ? eur(c.cout_reel_ht) : "•••"}
                    </td>
                    <td className={`px-3 py-3 text-right font-mono-metric font-semibold ${margeText(c.taux_marge_reel_pct)}`}>
                      {canSeeMargins ? pct(c.taux_marge_reel_pct) : "•••"}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}
